import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { DatePicker, Select, Space, Typography } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { FilterSelect, FormSelect } from 'components/Custom/selects';
import { ScopeTable } from 'components/Custom/table';
import dayjs from 'dayjs';
import { getLocalizedDatePickerFormat } from 'i18n/calendarLabels';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DateOptions } from 'store/api/enums';
import { getDateOptionLabel } from 'utils/display-enum-label';
import { useNumberFormat } from 'utils/numberFormat';
import { BaseCard } from './BaseCard';
import { TimeToSubmitCardProps } from './CardProps';
import { DASHBOARD_CARD_TITLE_KEYS } from './dashboardCardKeys';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const dateFormat = 'DD/MM/YYYY';
const DROPDOWN_OPEN_GUARD_MS = 250;

export const TimeToSubmitCard: React.FC<TimeToSubmitCardProps> = ({
  dashboardViewType,
  cardId,
  timeToSubmitMetrics,
  timeToSubmitDateRange,
  timeToSubmitDateRangeOption,
  enableTimeToSubmitCustomRange,
  timeToSubmitDateFilterOpen,
  setTimeToSubmitDateRange,
  onTimeToSubmitDateOptionSelect,
  onTimeToSubmitCustomRangeCheckboxChange,
  handleApplyTimeToSubmitDateFilter,
  handleCancelTimeToSubmitDateFilter,
  handleOpenTimeToSubmitDateFilterDropdown,
  t,
}) => {
  const { i18n } = useTranslation();
  const { formatNumber } = useNumberFormat();
  const dropdownOpenedAtRef = React.useRef(0);

  const renderSortIcon = ({ sortOrder }: { sortOrder?: string }) => {
    if (sortOrder === 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  };

  const handleFilterOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) {
        dropdownOpenedAtRef.current = Date.now();
      } else if (
        Date.now() - dropdownOpenedAtRef.current <
        DROPDOWN_OPEN_GUARD_MS
      ) {
        return;
      }
      handleOpenTimeToSubmitDateFilterDropdown(open);
    },
    [handleOpenTimeToSubmitDateFilterDropdown]
  );

  // Only show in Manager View
  if (dashboardViewType !== 'manager') return null;

  const cardTitle = t(DASHBOARD_CARD_TITLE_KEYS[cardId]!);

  return (
    <BaseCard
      cardId={cardId}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text style={{ fontSize: '16px', fontWeight: 600 }}>{cardTitle}</Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Text
              style={{
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '12px',
                textAlign: 'left',
                color: 'var(--color-text-dark, #042a2a)',
                minWidth: 35,
              }}
            >
              {t('main.filters')}
            </Text>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
              }}
            >
              <style>{`
                .time-to-submit-date-filter-dropdown.ant-select-dropdown {
                  min-width: 380px !important;
                  width: max-content !important;
                  max-width: 90vw !important;
                  height: auto !important;
                  max-height: none !important;
                  overflow: visible !important;
                }
              `}</style>
              <FilterSelect
                placeholder={t('main.submissionDate')}
                aria-label={t('main.submissionDate')}
                classNames={{
                  popup: { root: 'time-to-submit-date-filter-dropdown' },
                }}
                styles={{
                  popup: {
                    root: {
                      minWidth: 380,
                      width: 'max-content',
                      maxWidth: '90vw',
                      height: 'auto',
                      maxHeight: 'none',
                      overflow: 'visible',
                      fontSize: 13,
                      borderRadius: 20,
                      zIndex: 1100,
                    },
                  },
                }}
                popupRender={() => (
                  <Space
                    className="dashboard-date-filter-panel"
                    direction="vertical"
                    style={{
                      padding: '20px',
                      width: '380px',
                      minWidth: '380px',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <FormSelect
                      className="dashboard-date-filter-form-select"
                      width={'100%'}
                      height={'35px'}
                      radius={'10px'}
                      isFilter={true}
                      placeholder={t('main.submissionDate')}
                      aria-label={t('main.submissionDate')}
                      style={{
                        backgroundColor: 'var(--color-surface, #F3F7F8)',
                        borderColor: '#cccccc',
                        fontSize: 13,
                      }}
                      styles={{ popup: { fontSize: 13 } }}
                      onSelect={onTimeToSubmitDateOptionSelect}
                      value={timeToSubmitDateRangeOption}
                    >
                      {Object.values(DateOptions).map((value) => (
                        <Select.Option key={value} value={value}>
                          {getDateOptionLabel(value, t)}
                        </Select.Option>
                      ))}
                    </FormSelect>
                    <ScopeCheckbox
                      className="dashboard-date-filter-custom-range"
                      checked={enableTimeToSubmitCustomRange}
                      onChange={onTimeToSubmitCustomRangeCheckboxChange}
                    >
                      {t('main.custom-range')}
                    </ScopeCheckbox>
                    <RangePicker
                      className="dashboard-date-filter-range-picker"
                      style={{ borderRadius: '10px', height: '34px' }}
                      value={timeToSubmitDateRange}
                      disabled={!enableTimeToSubmitCustomRange}
                      format={getLocalizedDatePickerFormat(
                        dateFormat,
                        i18n.language
                      )}
                      onChange={(dates) =>
                        setTimeToSubmitDateRange(
                          dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]
                        )
                      }
                    />
                    <Space
                      style={{
                        float: 'inline-end',
                        marginTop: '10px',
                        width: '100%',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <ScopeButton
                        width="75px"
                        height="25px"
                        fontSize="12px"
                        lineheight="12px"
                        onClick={handleCancelTimeToSubmitDateFilter}
                        color={'#41AAAF'}
                        type="default"
                      >
                        {t('common.cancel')}
                      </ScopeButton>
                      <ScopeButton
                        width="75px"
                        height="25px"
                        fontSize="12px"
                        lineheight="12px"
                        onClick={handleApplyTimeToSubmitDateFilter}
                        type="primary"
                      >
                        {t('common.apply')}
                      </ScopeButton>
                    </Space>
                  </Space>
                )}
                onOpenChange={handleFilterOpenChange}
                open={timeToSubmitDateFilterOpen}
                value={
                  timeToSubmitDateRangeOption
                    ? getDateOptionLabel(String(timeToSubmitDateRangeOption), t)
                    : undefined
                }
                className={timeToSubmitDateRangeOption ? 'selected-filter' : ''}
              >
                <Select.Option key="time-to-submit-date">{''}</Select.Option>
              </FilterSelect>
            </div>
          </div>
        </div>
      }
      bodyStyle={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'auto',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <ScopeTable
          tableDataSource={timeToSubmitMetrics.map((metric, index) => ({
            ...metric,
            key: index,
          }))}
          headerFontSize={12}
          columns={[
            {
              title: t('main.preparer'),
              dataIndex: 'name',
              key: 'name',
              sorter: (a: any, b: any) =>
                (a.name || '').localeCompare(b.name || ''),
              sortIcon: renderSortIcon,
              render: (text: string) => (
                <Text style={{ fontSize: '14px', color: 'var(--color-text-dark, #042a2a)' }}>
                  {text}
                </Text>
              ),
            },
            {
              title: t('dashboard.avg-days'),
              dataIndex: 'avgDaysToSubmit',
              key: 'avgDaysToSubmit',
              align: 'center' as const,
              render: (value: number) => (
                <Text
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-dark, #042a2a)',
                    fontWeight: 600,
                  }}
                >
                  {formatNumber(value, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                </Text>
              ),
              sorter: (a: any, b: any) => a.avgDaysToSubmit - b.avgDaysToSubmit,
              sortIcon: renderSortIcon,
            },
            {
              title: t('main.submissions'),
              dataIndex: 'totalSubmissions',
              key: 'totalSubmissions',
              align: 'center' as const,
              render: (value: number) => (
                <Text
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-dark, #042a2a)',
                    fontWeight: 600,
                  }}
                >
                  {formatNumber(value, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </Text>
              ),
              sorter: (a: any, b: any) =>
                a.totalSubmissions - b.totalSubmissions,
              sortIcon: renderSortIcon,
            },
            {
              title: t('dashboard.fastest-days'),
              dataIndex: 'fastestSubmission',
              key: 'fastestSubmission',
              align: 'center' as const,
              render: (value: number | null) => (
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: value !== null && value <= 1 ? '#52c41a' : 'var(--color-text-dark, #042a2a)',
                  }}
                >
                  {value !== null
                    ? formatNumber(value, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : '-'}
                </Text>
              ),
              sorter: (a: any, b: any) => {
                if (a.fastestSubmission === null) return 1;
                if (b.fastestSubmission === null) return -1;
                return a.fastestSubmission - b.fastestSubmission;
              },
              sortIcon: renderSortIcon,
            },
            {
              title: t('dashboard.slowest-days'),
              dataIndex: 'slowestSubmission',
              key: 'slowestSubmission',
              align: 'center' as const,
              render: (value: number | null) => (
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: value !== null && value > 30 ? '#ff4d4f' : 'var(--color-text-dark, #042a2a)',
                  }}
                >
                  {value !== null
                    ? formatNumber(value, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : '-'}
                </Text>
              ),
              sorter: (a: any, b: any) => {
                if (a.slowestSubmission === null) return 1;
                if (b.slowestSubmission === null) return -1;
                return a.slowestSubmission - b.slowestSubmission;
              },
              sortIcon: renderSortIcon,
            },
            {
              title: t('dashboard.this-month'),
              dataIndex: 'submissionsThisMonth',
              key: 'submissionsThisMonth',
              align: 'center' as const,
              render: (value: number) => (
                <Text
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-dark, #042a2a)',
                    fontWeight: 600,
                  }}
                >
                  {formatNumber(value, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </Text>
              ),
              sorter: (a: any, b: any) =>
                a.submissionsThisMonth - b.submissionsThisMonth,
              sortIcon: renderSortIcon,
            },
            {
              title: t('dashboard.this-week'),
              dataIndex: 'submissionsThisWeek',
              key: 'submissionsThisWeek',
              align: 'center' as const,
              render: (value: number) => (
                <Text
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-dark, #042a2a)',
                    fontWeight: 600,
                  }}
                >
                  {formatNumber(value, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </Text>
              ),
              sorter: (a: any, b: any) =>
                a.submissionsThisWeek - b.submissionsThisWeek,
              sortIcon: renderSortIcon,
            },
          ]}
          pagination={false}
          rowsPerPage={timeToSubmitMetrics.length}
          enableColumnsCustomization={false}
          rowHeight={40}
          fontSize={12}
          showSizeChanger={false}
          getNewPageSize={() => {}}
          transactionType=""
          reportId=""
          scroll={{ x: 'max-content' }}
        />
      </div>
    </BaseCard>
  );
};
